import { javascript } from "@codemirror/lang-javascript";
import { json as jsonLanguage } from "@codemirror/lang-json";
import { python } from "@codemirror/lang-python";
import { Decoration, type DecorationSet, EditorView, type Extension, type Range, StateEffect, StateField } from "@uiw/react-codemirror";
import { EDITOR_SURFACE } from "../../common/editor-theme";
import type { LanguageId } from "../../common/schema/languages";

// What a problem covers in the payload, worked out from the pointer it names by the parse that read the payload.
export interface Mark {
  from: number;
  to: number;
  message: string;
}

// Handed to the payload editor whenever the schema, the payload or the language has moved what is wrong with it.
export const setMarks = StateEffect.define<Mark[]>();

export function replaceDoc(view: EditorView | null, text: string) {
  // Dispatched rather than fed back as a value, which keeps a generated document undoable like any other edit.
  view?.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
}

const PROBLEM_THEME = EditorView.theme({
  // Qualified with `.cm-line` so the tint beats the active line whatever order the two themes are injected in.
  ".cm-line.cm-schema-problem-line": { backgroundColor: "rgba(224, 49, 49, 0.12)" },
  ".cm-schema-problem": {
    textDecoration: "underline wavy var(--mantine-color-red-5)",
    textDecorationSkipInk: "none",
    textUnderlineOffset: "3px",
  },
});

const PROBLEM_LINE = Decoration.line({ class: "cm-schema-problem-line" });

const marksField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(marks, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setMarks)) return decorationsFor(tr.state.doc, effect.value);
    }
    // A keystroke carries the standing marks along with the text they were drawn over; the fresh set lands a render
    // later, and mapping them is what keeps the underline from blinking out between the two.
    return tr.docChanged ? marks.map(tr.changes) : marks;
  },
  provide: (field) => EditorView.decorations.from(field),
});

function decorationsFor(doc: { length: number; lineAt: (at: number) => { from: number; to: number } }, marks: Mark[]) {
  const ranges: Range<Decoration>[] = [];
  const lines = new Set<number>();

  for (const mark of marks) {
    const from = Math.max(0, Math.min(mark.from, doc.length));
    const to = Math.max(from, Math.min(mark.to, doc.length));
    const line = doc.lineAt(from);

    // The line the problem starts on is tinted whatever it covers, which is what a whole object being wrong looks
    // like; the underline is only drawn where it would fit on that line, an object spanning twenty being worse
    // underlined end to end than left to the tint alone.
    if (!lines.has(line.from)) {
      lines.add(line.from);
      ranges.push(PROBLEM_LINE.range(line.from));
    }
    if (to > from && to <= line.to) {
      ranges.push(Decoration.mark({ class: "cm-schema-problem", attributes: { title: mark.message } }).range(from, to));
    }
  }

  // Line decorations sort ahead of the marks starting at the same offset, so one sorted set holds both.
  return Decoration.set(ranges, true);
}

// Out here so their identity never changes: a new array reconfigures the running editor.
const BASE = [EditorView.lineWrapping, ...EDITOR_SURFACE];

export const PAYLOAD_EXTENSIONS: Extension[] = [
  marksField,
  PROBLEM_THEME,
  jsonLanguage(),
  EditorView.contentAttributes.of({ "aria-label": "JSON payload" }),
  ...BASE,
];

// One array per language, built once: the mode is part of the editor's configuration and not of its document.
const SCHEMA_EXTENSIONS: Record<LanguageId, Extension[]> = {
  "json-schema": [jsonLanguage(), EditorView.contentAttributes.of({ "aria-label": "JSON Schema" }), ...BASE],
  zod: [
    javascript({ typescript: true }),
    EditorView.contentAttributes.of({ "aria-label": "Zod schema" }),
    ...BASE,
  ],
  pydantic: [python(), EditorView.contentAttributes.of({ "aria-label": "Pydantic model" }), ...BASE],
};

export function schemaExtensions(language: LanguageId): Extension[] {
  return SCHEMA_EXTENSIONS[language];
}
